import { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  IconShieldLock,
  IconMail,
  IconSend,
  IconUserPlus,
  IconTrash
} from '@tabler/icons-react';
import { apiGlobal } from '../services/api';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useToast } from '../components/ToastNotification';
import { CenteredLoader } from '../components/LoadingIndicator';
import { PageHero, SectionContainer, GroupContainer, Badge } from '../components/ds';
import TipsManagerCard from '../components/admin/TipsManagerCard.jsx';
import UpdatesManagerCard from '../components/admin/UpdatesManagerCard.jsx';
import ExportsManagerCard from '../components/admin/ExportsManagerCard.jsx';

// Tier display labels map to the lowercase API tiers (kit TierSelect).
const TIERS = [
  { value: 'beta', label: 'Beta' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'trial', label: 'Trial' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// The seed script stamps addedBy as "seed-script (arn:aws:sts::...)"; the raw
// ARN is operator noise and a long unbreakable token. Collapse it to the
// leading label for the roster (in-app adds use the admin's email, unchanged).
const formatAddedBy = (addedBy) => {
  if (!addedBy) return 'System';
  return addedBy.replace(/\s*\(.*\)\s*$/, '');
};

/**
 * Application admin page (#1427, V4_ADDENDUM convention 13). Mirrors
 * templates/desktop-app-admin + templates/mobile-app-admin: beta invites
 * (form + outstanding list with convention-7 revoke) and the application
 * admin roster (add-by-email; self/last-admin removal blocked). Admin-only:
 * non-admins are redirected to the Dashboard as if the page does not exist;
 * the backend gate on /api/admin/* is the real enforcement.
 */
const AppAdmin = ({ isAuthReady = false }) => {
  const theme = useTheme();
  const { palette, ds } = theme;
  const session = useUserSession();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tier, setTier] = useState('beta');
  const [adminEmail, setAdminEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const isAdmin = !!session?.isAppAdmin;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, admRes] = await Promise.all([
        apiGlobal.get('/admin/invites'),
        apiGlobal.get('/admin/admins'),
      ]);
      setInvites(invRes.invites || []);
      setAdmins(admRes.admins || []);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthReady && isAdmin) loadData();
  }, [isAuthReady, isAdmin, loadData]);

  // Convention 13: reached by a non-admin, the page redirects to the
  // Dashboard as if it does not exist (no 404, no message).
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleSendInvite = async () => {
    setBusy(true);
    try {
      const res = await apiGlobal.post('/admin/invites', {
        email: email.trim(),
        name: name.trim() || undefined,
        tier,
      });
      showSuccess(res.emailSent
        ? 'Invite staged and email sent'
        : 'Invite staged; the email could not be sent');
      setEmail('');
      setName('');
      setTier('beta');
      await loadData();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (inviteEmail) => {
    setBusy(true);
    try {
      await apiGlobal.delete(`/admin/invites/${encodeURIComponent(inviteEmail)}`);
      showSuccess('Invite revoked');
      setConfirmRevoke(null);
      await loadData();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAddAdmin = async () => {
    setBusy(true);
    try {
      const res = await apiGlobal.post('/admin/admins', { email: adminEmail.trim() });
      showSuccess(res.alreadyAdmin ? 'Already an admin' : 'Admin added');
      setAdminEmail('');
      await loadData();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAdmin = async (internalUserId) => {
    setBusy(true);
    try {
      await apiGlobal.delete(`/admin/admins/${encodeURIComponent(internalUserId)}`);
      showSuccess('Admin removed');
      setConfirmRemove(null);
      await loadData();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const hairline = `1px solid ${palette.divider}`;

  // Kit TextInput look: surface-2 fill, hairline that turns buttonPrimary on
  // focus. xs is >=16px so focusing never zooms iOS (convention 12); desktop
  // reads at 14px.
  const fieldSx = {
    '& .MuiInputBase-root': {
      backgroundColor: palette.surface.hover,
      fontSize: { xs: '16px', sm: '14px' },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.divider },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.borderStrong },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.buttonPrimary.bg },
  };

  // Convention 7 revoke: always-visible grey trash that reddens on hover.
  const trashButton = (title, onClick) => (
    <IconButton
      size="small"
      aria-label={title}
      title={title}
      onClick={onClick}
      disabled={busy}
      sx={{
        color: palette.text.dim,
        '&:hover': { color: palette.error.main, backgroundColor: 'transparent' },
        flexShrink: 0,
      }}
    >
      <IconTrash size={16} />
    </IconButton>
  );

  const disabledTrash = (title) => (
    <Box
      component="span"
      title={title}
      aria-label={title}
      sx={{ color: palette.text.faint, opacity: 0.5, cursor: 'not-allowed', display: 'inline-flex', flexShrink: 0, padding: '5px' }}
    >
      <IconTrash size={16} />
    </Box>
  );

  const inviteCard = (
    <SectionContainer title="Invite a beta user" category="budgets">
      <Box sx={{ padding: '12px 0 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <TextField
          size="small"
          type="email"
          placeholder="name@email.com"
          inputProps={{ 'aria-label': 'Invite email' }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={fieldSx}
          fullWidth
        />
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
          <TextField
            size="small"
            placeholder="Name (optional)"
            inputProps={{ 'aria-label': 'Invite name' }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ ...fieldSx, flex: isMobile ? undefined : 1.3 }}
          />
          <Select
            size="small"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            inputProps={{ 'aria-label': 'Invite tier' }}
            sx={{
              width: isMobile ? '100%' : 150,
              backgroundColor: palette.surface.hover,
              fontSize: { xs: '16px', sm: '14px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.divider },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.borderStrong },
            }}
          >
            {TIERS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            startIcon={<IconSend size={16} />}
            disabled={email.trim().length === 0 || busy}
            onClick={handleSendInvite}
            fullWidth={isMobile}
          >
            Send invite
          </Button>
        </Box>
        <Box sx={{ fontSize: '12px', color: palette.text.dim }}>
          Stages a pending grant and emails a sign-in link. Unredeemed invites expire after 14 days.
        </Box>
      </Box>
    </SectionContainer>
  );

  const outstandingCard = (
    <SectionContainer title="Outstanding invitations" category="warning" count={invites.length}>
      {invites.length === 0 ? (
        <Box sx={{ padding: '12px 0', fontSize: '13px', color: palette.text.dim }}>
          No outstanding invitations.
        </Box>
      ) : (
        invites.map((inv, i) => (
          <Box
            key={inv.email}
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: '11px',
              padding: isMobile ? '11px 0' : '10px 0',
              borderBottom: i === invites.length - 1 ? 'none' : hairline,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: `${ds.radius.icon}px`,
                backgroundColor: palette.tint.neutral,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconMail size={16} color={palette.cat.neutral} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ color: palette.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {inv.email}
              </Box>
              <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '1px' }}>
                {`Invited ${formatDate(inv.createdAt)} · expires ${formatDate(inv.expiresAt)}`}
              </Box>
              {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                  <Badge variant="neutral">{inv.tier}</Badge>
                  <Badge variant="unreconciled">pending</Badge>
                </Box>
              )}
            </Box>
            {!isMobile && (
              <>
                <Badge variant="neutral">{inv.tier}</Badge>
                <Badge variant="unreconciled">pending</Badge>
              </>
            )}
            {trashButton('Revoke invite', () => setConfirmRevoke(inv.email))}
          </Box>
        ))
      )}
    </SectionContainer>
  );

  const adminsCard = (
    <SectionContainer title="Application admins" category="interactive" count={admins.length}>
      {admins.map((a) => {
        const isSelf = a.email === session?.user?.email;
        const isLast = admins.length <= 1;
        const blocked = isSelf || isLast;
        const blockedTitle = isLast ? "Can't remove the last admin" : "Can't remove yourself";
        return (
          <Box
            key={a.internalUserId}
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: '11px',
              padding: isMobile ? '11px 0' : '10px 0',
              borderBottom: hairline,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${palette.interactiveStrong}, ${palette.accentDeep})`,
                color: palette.interactiveOn,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: ds.font.weightMedium,
                flexShrink: 0,
              }}
            >
              {(a.name || a.email || '?').charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ color: palette.text.primary }}>
                {a.name}
                {isSelf && <Box component="span" sx={{ color: palette.text.dim }}>{' (you)'}</Box>}
              </Box>
              <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.email}
              </Box>
              {isMobile && (
                <Box sx={{ fontSize: '12px', color: palette.text.dim, marginTop: '2px', overflowWrap: 'anywhere' }}>
                  {`Added by ${formatAddedBy(a.addedBy)} · ${formatDate(a.addedAt)}`}
                </Box>
              )}
            </Box>
            {!isMobile && (
              <Box sx={{ textAlign: 'right', fontSize: '12px', color: palette.text.dim, whiteSpace: 'nowrap' }}>
                <div>{`Added by ${formatAddedBy(a.addedBy)}`}</div>
                <Box sx={{ marginTop: '1px' }}>{formatDate(a.addedAt)}</Box>
              </Box>
            )}
            {blocked
              ? disabledTrash(blockedTitle)
              : trashButton('Remove admin', () => setConfirmRemove(a))}
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', padding: '12px 0 8px' }}>
        <TextField
          size="small"
          type="email"
          placeholder="Add admin by email"
          inputProps={{ 'aria-label': 'Add admin email' }}
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          sx={{ ...fieldSx, flex: isMobile ? undefined : 1 }}
        />
        <Button
          variant="contained"
          startIcon={<IconUserPlus size={16} />}
          disabled={adminEmail.trim().length === 0 || busy}
          onClick={handleAddAdmin}
          fullWidth={isMobile}
        >
          Add admin
        </Button>
      </Box>
      <Box sx={{ fontSize: '12px', color: palette.text.dim, paddingBottom: '4px' }}>
        {admins.length <= 1
          ? 'Admins get full access to this page. You are the only admin, so removal is disabled.'
          : 'Admins get full access to this page.'}
      </Box>
    </SectionContainer>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <PageHero
        label="Application admin"
        back={{ label: 'Dashboard', onClick: () => navigate('/') }}
        aux={
          isMobile ? (
            <IconShieldLock size={16} color={palette.interactive} aria-label="Restricted" />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: palette.text.dim }}>
              <IconShieldLock size={14} color={palette.interactive} />
              Restricted to application admins
            </Box>
          )
        }
      />

      {loading ? (
        <CenteredLoader message="Loading admin data..." />
      ) : (
        <Box
          sx={{
            mt: { xs: '12px', md: '14px' },
            display: 'grid',
            // Two independent column stacks (DESIGN_SYSTEM.md 8.13, #1863):
            // every card of a group stays on one side, and the group with the
            // widest content (Exports) takes the wider column. minmax(0, ...)
            // + minWidth:0 children so a wide descendant (a long addedBy ARN,
            // an email) shrinks/wraps instead of widening the document past
            // the viewport (mirrors Members.jsx).
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.25fr)' },
            gap: '16px',
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px', minWidth: 0 }}>
            <GroupContainer
              title="Access"
              subtitle="Who can sign in as a beta user and who can administer the app."
            >
              {inviteCard}
              {outstandingCard}
              {adminsCard}
            </GroupContainer>
            {/* Tips manager (#1720): Tips + Email Summary Config cards, and
                the Feature Spotlight updates card (#1875). */}
            <GroupContainer
              title="Messaging"
              subtitle="Rotating in-app tips, product updates, and the subject line of the summary email."
            >
              <TipsManagerCard />
              <UpdatesManagerCard />
            </GroupContainer>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px', minWidth: 0 }}>
            {/* Export pipeline (#1853): Queued, Running, History, Settings. */}
            <GroupContainer
              title="Exports"
              subtitle="Queued, running and finished workspace exports, and the daily windows in which the batch runs."
            >
              <ExportsManagerCard />
            </GroupContainer>
          </Box>
        </Box>
      )}

      <Dialog open={!!confirmRevoke} onClose={() => !busy && setConfirmRevoke(null)}>
        <DialogTitle>Revoke invite</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Revoke the invite for ${confirmRevoke}? They will no longer be able to sign up.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevoke(null)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={() => handleRevoke(confirmRevoke)} disabled={busy}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRemove} onClose={() => !busy && setConfirmRemove(null)}>
        <DialogTitle>Remove admin</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Remove ${confirmRemove?.name || confirmRemove?.email} from the application admins?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={() => handleRemoveAdmin(confirmRemove?.internalUserId)} disabled={busy}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppAdmin;
